import React, { useEffect } from "react";
import { Button, Heading, Text, View, Flex } from "@aws-amplify/ui-react";
import {
    LAPS_PER_DAY,
    NUDGE_DAY_COMPLETE,
    NUDGE_LAP_COMPLETE,
    DAILY_ENGAGE_TARGET,
    MAX_SCHEDULE_AHEAD_DAYS,
} from "./taskLapModel";

// Tiny CSS-only confetti burst — no extra deps.
function ConfettiBurst() {
    const pieces = Array.from({ length: 28 });
    const colors = ["#fe60a1", "#ff8765", "#0074e4", "#67aaf1", "#ffd24d", "#7be0a4"];
    return (
        <div
            aria-hidden="true"
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                overflow: "hidden",
            }}
        >
            <style>{`
                @keyframes gcConfettiFall {
                    0%   { transform: translate3d(0, -10px, 0) rotate(0deg);  opacity: 1; }
                    100% { transform: translate3d(var(--gc-x, 0), 240px, 0) rotate(540deg); opacity: 0; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .gc-confetti-piece { animation: none !important; opacity: 0 !important; }
                }
            `}</style>
            {pieces.map((_, i) => {
                const delay = (i % 8) * 60;
                const x = (Math.random() - 0.5) * 280; // -140..140 px
                const left = 10 + Math.random() * 80; // 10%..90%
                const color = colors[i % colors.length];
                const size = 6 + Math.floor(Math.random() * 6);
                return (
                    <span
                        key={i}
                        className="gc-confetti-piece"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: `${left}%`,
                            width: size,
                            height: size,
                            background: color,
                            borderRadius: "2px",
                            // CSS variable consumed by the keyframes
                            ["--gc-x"]: `${x}px`,
                            animation: `gcConfettiFall 1400ms cubic-bezier(.2,.7,.2,1) ${delay}ms both`,
                        }}
                    />
                );
            })}
        </div>
    );
}

// Per-flow wording. This is the ONLY thing that differs between ENGAGE and GROW nudges —
// the trigger rule, the layout, the confetti rule and the CTA are shared (taskLapModel.js).
const KIND_COPY = {
    engage: {
        goal: "ENGAGE",
        unit: "engagements",
        taskNoun: "ENGAGE tasks",
    },
    grow: {
        goal: "GROW",
        unit: "connection requests",
        taskNoun: "GROW connection requests",
    },
    posts: { goal: "POST" },
};

/**
 * Soft pause nudge, shared by the ENGAGE and GROW flows.
 *
 *   variant="lap-complete" -> first lap of `lapSize` done: confetti + "another lap" CTA
 *   variant="day-complete" -> both laps done: ask the user to come back later (no lock)
 *
 * `kind` ("engage" | "grow") only swaps the wording; `lapSize` supplies every number in the
 * copy, so nothing here has to be edited when a target changes.
 *
 * Theming follows the same hostname rule as TaskDonutCharts.
 */
export default function TaskNudgeModal({
    open,
    kind = "engage",
    variant,
    lapSize,
    coveredThrough,
    onClose,
}) {
    const isFirstInfluencerDomain =
        typeof window !== "undefined" &&
        window.location.hostname.includes("1stinfluencer");
    const accent = isFirstInfluencerDomain ? "#0074e4" : "#fe60a1";
    const accent2 = isFirstInfluencerDomain ? "#67aaf1" : "#ff8765";

    // ESC to close
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const copy = KIND_COPY[kind] || KIND_COPY.engage;
    const isPosts = kind === "posts";
    const isLapComplete = (variant || NUDGE_LAP_COMPLETE) !== NUDGE_DAY_COMPLETE;
    const perLap = Number.isFinite(Number(lapSize)) && Number(lapSize) > 0
        ? Math.floor(Number(lapSize))
        : DAILY_ENGAGE_TARGET;
    const perDay = perLap * LAPS_PER_DAY;

    const coveredDate = Number.isFinite(new Date(coveredThrough).getTime())
        ? new Date(coveredThrough).toLocaleDateString()
        : "tomorrow";

    const title = isPosts
        ? "Post scheduled!"
        : isLapComplete
        ? `Nice — ${perLap} ${copy.unit} today!`
        : "Time for a break";

    const body = isPosts ? (
        <>
            You're covered through {coveredDate}. Each post adds a day — you can build up
            to {MAX_SCHEDULE_AHEAD_DAYS} days of coverage ahead.
        </>
    ) : isLapComplete ? (
        <>
            You hit your daily {copy.goal} goal. You can keep going for a second lap of{" "}
            {perLap}, but we recommend pacing yourself to keep activity natural on LinkedIn.
        </>
    ) : (
        <>
            You have completed {perDay} {copy.taskNoun} today. Please consider coming back in a
            few hours so your activity looks natural. We won't lock you out, but bursts above
            this level can attract LinkedIn's attention.
        </>
    );

    const cta = isPosts ? "Got it" : isLapComplete ? "OK, second lap" : "Got it";

    return (
        <View
            role="dialog"
            aria-modal="true"
            aria-labelledby="gc-nudge-title"
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 18, 28, 0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
                padding: "1rem",
            }}
            onClick={onClose}
        >
            <View
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: "relative",
                    background: "white",
                    borderRadius: 16,
                    width: "100%",
                    maxWidth: 420,
                    padding: "1.5rem 1.25rem 1.25rem",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                    borderTop: `6px solid ${accent}`,
                    overflow: "hidden",
                }}
            >
                {isLapComplete || isPosts ? <ConfettiBurst /> : null}
                <Heading
                    id="gc-nudge-title"
                    level={4}
                    style={{ color: accent, marginBottom: "0.5rem", textAlign: "center" }}
                >
                    {title}
                </Heading>
                <Text
                    style={{
                        color: "#333",
                        textAlign: "center",
                        lineHeight: 1.45,
                        marginBottom: "1.25rem",
                    }}
                >
                    {body}
                </Text>
                <Flex justifyContent="center">
                    <Button
                        onClick={onClose}
                        style={{
                            background: `linear-gradient(135deg, ${accent}, ${accent2})`,
                            color: "white",
                            border: "none",
                            paddingInline: "1.5rem",
                        }}
                    >
                        {cta}
                    </Button>
                </Flex>
            </View>
        </View>
    );
}
